import _ from 'lodash';
import { ApolloError } from '@apollo/client';

import { ProductGroupFragment, useGetAudienceProductGroupsQuery } from '@occc/graphql';
import { UseGetAudienceSegmentsReturns, useGetAudienceSegments } from '@occc/feat-segments';

export type UseGetAudienceProductGroupsAndSegmentsByIdsParams = {
  chainIdEquals?: string;
  productGroupIds: string[];
  segmentUuids: string[];
};

type UseGetAudienceProductGroupsAndSegmentsByIdsReturns = {
  loading: boolean;
  error?: ApolloError;
  data: {
    productGroups: ProductGroupFragment[];
    segments: UseGetAudienceSegmentsReturns['segments'];
  };
  loaded: boolean;
};

export const useGetAudienceProductGroupsAndSegmentsByIds = ({
  segmentUuids,
  productGroupIds,
  chainIdEquals,
}: UseGetAudienceProductGroupsAndSegmentsByIdsParams): UseGetAudienceProductGroupsAndSegmentsByIdsReturns => {
  const shouldLoadProductGroups = Boolean(productGroupIds.length);
  const shouldLoadSegments = Boolean(segmentUuids.length);

  const productGroupsResponse = useGetAudienceProductGroupsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !shouldLoadProductGroups,
    variables: {
      filter: {
        idContains: productGroupIds || [],
        ...(chainIdEquals ? { chainIdEquals } : {}),
        isDeleted: false,
      },
    },
  });

  const { loading: loadingSegments, segments } = useGetAudienceSegments({
    segmentUuids,
    chainIdEquals,
  });

  const productGroups = productGroupsResponse.data?.productGroups
    ? _.map(productGroupsResponse.data.productGroups.edges, 'node')
    : [];

  const loadedProductGroups =
    !shouldLoadProductGroups || Boolean(productGroupsResponse.data?.productGroups);
  const loadedSegments = !shouldLoadSegments || Boolean(segments);

  return {
    loading: productGroupsResponse.loading || loadingSegments,
    data: {
      productGroups,
      segments,
    },
    loaded: loadedProductGroups && loadedSegments,
  };
};
